// js/settings.js — Settings tab: manage projects and categories

import * as Data from './data.js';
import { exportAllCSV } from './export.js';
import { isDark, buildSettingsToggle } from './theme.js';

export async function renderSettings() {
  const c = document.getElementById('view-settings');
  c.innerHTML = `
    <h2 class="text-lg font-semibold mb-6">Settings</h2>
    <div id="settings-appearance" class="mb-8"></div>
    <div id="settings-username" class="mb-8"></div>
    <div class="grid grid-cols-1 gap-8 lg:grid-cols-2">
      <div id="settings-projects"></div>
      <div id="settings-categories"></div>
    </div>
    <div id="settings-custom-holidays" class="mt-8"></div>
    <div id="settings-data" class="mt-8"></div>
  `;
  renderAppearance();
  renderUsername();
  await renderProjects();
  await renderCategories();
  await renderCustomHolidays();
  renderDataExport();
}

// ── Appearance ────────────────────────────────────────────────────────────────

function renderAppearance() {
  const pane = document.getElementById('settings-appearance');
  const wrap = document.createElement('div');
  wrap.className = 'bg-white rounded-xl border border-gray-200 p-4 max-w-sm';
  wrap.innerHTML = `<h3 class="font-semibold text-gray-700 mb-3">Appearance</h3>`;

  const row = document.createElement('div');
  row.className = 'flex items-center justify-between';

  const label = document.createElement('div');
  label.innerHTML = `
    <p class="text-sm text-gray-700">Dark mode</p>
    <p class="text-xs text-gray-400 mt-0.5">Light mode by default</p>
  `;

  const toggle = buildSettingsToggle();

  row.appendChild(label);
  row.appendChild(toggle);
  wrap.appendChild(row);
  pane.appendChild(wrap);
}

// ── Projects ──────────────────────────────────────────────────────────────────

function renderUsername() {
  const pane = document.getElementById('settings-username');
  const current = localStorage.getItem('tl_username') || '';
  pane.innerHTML = `
    <div class="bg-white rounded-xl border border-gray-200 p-4 max-w-sm">
      <h3 class="font-semibold text-gray-700 mb-3">Your Name</h3>
      <div class="flex gap-2">
        <input id="username-field" type="text" class="cell-input text-sm flex-1" value="${current}" placeholder="Your name" />
        <button id="username-save" class="text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700">Save</button>
      </div>
      <p class="text-xs text-gray-400 mt-2">This name appears on your time entries in shared reports.</p>
    </div>
  `;
  pane.querySelector('#username-save').addEventListener('click', () => {
    const val = pane.querySelector('#username-field').value.trim();
    if (!val) return;
    localStorage.setItem('tl_username', val);
    const savedMsg = document.createElement('span');
    savedMsg.className = 'text-xs text-green-600 ml-2';
    savedMsg.textContent = '✓ Saved';
    pane.querySelector('#username-save').after(savedMsg);
    setTimeout(() => savedMsg.remove(), 2000);
  });
}

async function renderProjects() {
  const pane = document.getElementById('settings-projects');
  const projects = await Data.getProjects();

  pane.innerHTML = `
    <div class="bg-white rounded-xl border border-gray-200 p-4">
      <div class="flex items-center justify-between mb-3">
        <h3 class="font-semibold text-gray-700">Projects</h3>
        <button id="btn-add-project" class="text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700">+ Add Project</button>
      </div>
      <table class="w-full text-sm border-collapse" id="projects-table">
        <thead>
          <tr class="border-b text-left text-xs text-gray-400 uppercase tracking-wider">
            <th class="py-2 pr-3 font-medium">Name</th>
            <th class="py-2 pr-3 font-medium text-center">Active</th>
            <th class="py-2 pr-3 font-medium text-center">Billable</th>
            <th></th>
          </tr>
        </thead>
        <tbody></tbody>
      </table>
    </div>
  `;

  const tbody = pane.querySelector('tbody');
  projects.forEach(project => tbody.appendChild(buildProjectRow(project)));

  pane.querySelector('#btn-add-project').addEventListener('click', async () => {
    const p = await Data.saveProject({ name: 'New Project', active: true, billable: false });
    await renderProjects();
    // Activate name editing on new row
    setTimeout(() => {
      const cell = pane.querySelector(`[data-proj-id="${p.id}"] .name-cell`);
      if (cell) cell.click();
    }, 0);
  });
}

function buildProjectRow(project) {
  const tr = document.createElement('tr');
  tr.className = 'border-b border-gray-100 hover:bg-gray-50 group';
  tr.dataset.projId = project.id;

  // Name cell (inline editable)
  const nameTd = document.createElement('td');
  nameTd.className = 'py-2 pr-3 cell-editable name-cell';
  nameTd.textContent = project.name;
  nameTd.addEventListener('click', () => activateProjectNameEdit(nameTd, project));
  tr.appendChild(nameTd);

  // Active toggle
  const activeTd = document.createElement('td');
  activeTd.className = 'py-2 pr-3 text-center';
  const toggle = document.createElement('input');
  toggle.type = 'checkbox';
  toggle.checked = project.active;
  toggle.className = 'cursor-pointer w-4 h-4 accent-blue-600';
  toggle.addEventListener('change', async () => {
    await Data.saveProject({ ...project, active: toggle.checked });
    await renderProjects();
  });
  activeTd.appendChild(toggle);
  tr.appendChild(activeTd);

  // Billable toggle
  const billableTd = document.createElement('td');
  billableTd.className = 'py-2 pr-3 text-center';
  const billableToggle = document.createElement('input');
  billableToggle.type = 'checkbox';
  billableToggle.checked = !!project.billable;
  billableToggle.className = 'cursor-pointer w-4 h-4 accent-blue-600';
  billableToggle.addEventListener('change', async () => {
    await Data.saveProject({ ...project, billable: billableToggle.checked });
    await renderProjects();
  });
  billableTd.appendChild(billableToggle);
  tr.appendChild(billableTd);

  // Delete
  const delTd = document.createElement('td');
  delTd.className = 'py-1';
  const delBtn = document.createElement('button');
  delBtn.className = 'text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 text-xs px-2 py-1 rounded hover:bg-red-50';
  delBtn.textContent = '✕';
  delBtn.addEventListener('click', async () => {
    // Close any other open reassignment rows first
    document.querySelectorAll('.project-reassign-row').forEach(r => r.remove());

    const usedByEntries = (await Data.getEntries(null, null)).filter(e => e.projectId === project.id);
    if (usedByEntries.length === 0) {
      if (confirm(`Delete project "${project.name}"?`)) {
        await Data.deleteProject(project.id);
        await renderProjects();
      }
      return;
    }

    // Build inline reassignment row
    const otherProjects = (await Data.getProjects()).filter(p => p.active && p.id !== project.id);
    const reassignRow = document.createElement('tr');
    reassignRow.className = 'project-reassign-row';
    reassignRow.style.cssText = 'background:#fffbeb;';
    const td = document.createElement('td');
    td.colSpan = 4;
    td.className = 'px-3 py-2';
    td.innerHTML = `
      <div class="flex items-center gap-2 flex-wrap">
        <span class="text-xs font-medium" style="color:#92400e">
          ⚠ ${usedByEntries.length} ${usedByEntries.length === 1 ? 'entry uses' : 'entries use'} this project. Reassign to:
        </span>
        <select class="cell-input text-xs" style="width:auto" id="reassign-select">
          <option value="">— No project —</option>
          ${otherProjects.map(p => `<option value="${p.id}">${p.name}</option>`).join('')}
        </select>
        <button id="btn-reassign-confirm" class="text-xs px-3 py-1 rounded text-white" style="background:#dc2626">Reassign & Delete</button>
        <button id="btn-reassign-cancel" class="text-xs px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-100">Cancel</button>
      </div>
    `;
    reassignRow.appendChild(td);
    tr.insertAdjacentElement('afterend', reassignRow);

    reassignRow.querySelector('#btn-reassign-confirm').addEventListener('click', async () => {
      const toId = reassignRow.querySelector('#reassign-select').value || null;
      await Data.reassignProjectEntries(project.id, toId);
      await Data.deleteProject(project.id);
      await renderProjects();
    });
    reassignRow.querySelector('#btn-reassign-cancel').addEventListener('click', () => {
      reassignRow.remove();
    });
  });
  delTd.appendChild(delBtn);
  tr.appendChild(delTd);

  return tr;
}

function activateProjectNameEdit(td, project) {
  const original = project.name;
  let cancelled = false;
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'cell-input';
  input.value = original;
  td.innerHTML = '';
  td.appendChild(input);
  input.focus();
  input.select();

  const commit = async () => {
    if (cancelled) return;
    const val = input.value.trim() || original;
    await Data.saveProject({ ...project, name: val });
    await renderProjects();
  };
  input.addEventListener('keydown', e => {
    if (e.key === 'Enter')  { commit(); input.blur(); }
    if (e.key === 'Escape') { cancelled = true; td.textContent = original; }
  });
  input.addEventListener('blur', commit);
}

// ── Categories ────────────────────────────────────────────────────────────────

async function renderCategories() {
  const pane = document.getElementById('settings-categories');
  const categories = await Data.getCategories();

  pane.innerHTML = `
    <div class="bg-white rounded-xl border border-gray-200 p-4">
      <div class="flex items-center justify-between mb-3">
        <h3 class="font-semibold text-gray-700">Categories</h3>
        <button id="btn-add-category" class="text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700">+ Add Category</button>
      </div>
      <div id="cat-error" class="text-red-500 text-xs mb-2 hidden"></div>
      <table class="w-full text-sm border-collapse">
        <thead>
          <tr class="border-b text-left text-xs text-gray-400 uppercase tracking-wider">
            <th class="py-2 pr-3 font-medium">Color</th>
            <th class="py-2 pr-3 font-medium">Name</th>
            <th class="py-2 pr-3 font-medium">Type</th>
            <th></th>
          </tr>
        </thead>
        <tbody id="cat-tbody"></tbody>
      </table>
    </div>
  `;

  const tbody = pane.querySelector('#cat-tbody');
  categories.forEach(cat => tbody.appendChild(buildCategoryRow(cat, pane)));

  pane.querySelector('#btn-add-category').addEventListener('click', async () => {
    const cat = await Data.saveCategory({ name: 'New Category', type: 'work', color: '#6b7280' });
    await renderCategories();
    setTimeout(() => {
      const cell = pane.querySelector(`[data-cat-id="${cat.id}"] .cat-name-cell`);
      if (cell) cell.click();
    }, 0);
  });
}

function buildCategoryRow(cat, pane) {
  const tr = document.createElement('tr');
  tr.className = 'border-b border-gray-100 hover:bg-gray-50 group';
  tr.dataset.catId = cat.id;

  // Color picker
  const colorTd = document.createElement('td');
  colorTd.className = 'py-2 pr-3';
  const colorInput = document.createElement('input');
  colorInput.type = 'color';
  colorInput.value = cat.color;
  colorInput.className = 'cursor-pointer w-8 h-8 rounded border border-gray-200 p-0.5';
  colorInput.addEventListener('change', async () => {
    await Data.saveCategory({ ...cat, color: colorInput.value });
    await renderCategories();
  });
  colorTd.appendChild(colorInput);
  tr.appendChild(colorTd);

  // Name cell (inline editable)
  const nameTd = document.createElement('td');
  nameTd.className = 'py-2 pr-3 cell-editable cat-name-cell';
  nameTd.textContent = cat.name;
  nameTd.addEventListener('click', () => activateCatNameEdit(nameTd, cat));
  tr.appendChild(nameTd);

  // Type select
  const typeTd = document.createElement('td');
  typeTd.className = 'py-2 pr-3';
  const typeSelect = document.createElement('select');
  typeSelect.className = 'cell-input w-auto';
  ['work', 'leave', 'holiday'].forEach(t => {
    const opt = document.createElement('option');
    opt.value = t;
    opt.textContent = t.charAt(0).toUpperCase() + t.slice(1);
    if (t === cat.type) opt.selected = true;
    typeSelect.appendChild(opt);
  });
  typeSelect.addEventListener('change', async () => {
    await Data.saveCategory({ ...cat, type: typeSelect.value });
    await renderCategories();
  });
  typeTd.appendChild(typeSelect);
  tr.appendChild(typeTd);

  // Delete
  const delTd = document.createElement('td');
  const delBtn = document.createElement('button');
  delBtn.className = 'text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 text-xs px-2 py-1 rounded hover:bg-red-50';
  delBtn.textContent = '✕';
  delBtn.addEventListener('click', async () => {
    const errEl = pane.querySelector('#cat-error');
    try {
      await Data.deleteCategory(cat.id);
      await renderCategories();
    } catch (err) {
      errEl.textContent = err.message;
      errEl.classList.remove('hidden');
      setTimeout(() => errEl.classList.add('hidden'), 4000);
    }
  });
  delTd.appendChild(delBtn);
  tr.appendChild(delTd);

  return tr;
}

// ── Custom Holidays ────────────────────────────────────────────────────────────

async function renderCustomHolidays() {
  const pane = document.getElementById('settings-custom-holidays');
  const holidays = await Data.getCustomHolidays();

  pane.innerHTML = `
    <div class="bg-white rounded-xl border border-gray-200 p-4">
      <h3 class="font-semibold text-gray-700 mb-1">Custom Holidays</h3>
      <p class="text-xs text-gray-400 mb-4">Add company-specific holidays (off-sites, office closures). A full-day Holiday entry is created automatically on the date.</p>
      <div class="flex gap-2 mb-3 items-center">
        <input type="date" id="ch-date" class="cell-input" style="width:160px;flex:none" />
        <input type="text" id="ch-name" class="cell-input flex-1" placeholder="e.g. Team Off-site" />
        <button id="btn-add-ch" class="text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 whitespace-nowrap">+ Add</button>
      </div>
      <div id="ch-error" class="text-red-500 text-xs mb-2 hidden"></div>
      ${holidays.length ? `
      <table class="w-full text-sm border-collapse">
        <thead>
          <tr class="border-b text-left text-xs text-gray-400 uppercase tracking-wider">
            <th class="py-2 pr-3 font-medium">Date</th>
            <th class="py-2 pr-3 font-medium">Name</th>
            <th></th>
          </tr>
        </thead>
        <tbody id="ch-tbody"></tbody>
      </table>` : '<p class="text-xs text-gray-400">No custom holidays added yet.</p>'}
    </div>
  `;

  const errEl = pane.querySelector('#ch-error');

  if (holidays.length) {
    const tbody = pane.querySelector('#ch-tbody');
    holidays.forEach(h => tbody.appendChild(buildCustomHolidayRow(h)));
  }

  pane.querySelector('#btn-add-ch').addEventListener('click', async () => {
    const dateInput = pane.querySelector('#ch-date');
    const nameInput = pane.querySelector('#ch-name');
    const date = dateInput.value.trim();
    const name = nameInput.value.trim();
    if (!date) { showChError(errEl, 'Please select a date.'); return; }
    if (!name) { showChError(errEl, 'Please enter a holiday name.'); return; }
    await Data.saveCustomHoliday({ date, name });
    await renderCustomHolidays();
  });
}

function showChError(el, msg) {
  el.textContent = msg;
  el.classList.remove('hidden');
  setTimeout(() => el.classList.add('hidden'), 4000);
}

function buildCustomHolidayRow(holiday) {
  const tr = document.createElement('tr');
  tr.className = 'border-b border-gray-100 hover:bg-gray-50 group';

  const dateTd = document.createElement('td');
  dateTd.className = 'py-2 pr-3 text-gray-600 whitespace-nowrap';
  const d = new Date(holiday.date + 'T00:00:00');
  dateTd.textContent = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  tr.appendChild(dateTd);

  const nameTd = document.createElement('td');
  nameTd.className = 'py-2 pr-3 font-medium';
  nameTd.textContent = holiday.name;
  tr.appendChild(nameTd);

  const delTd = document.createElement('td');
  delTd.className = 'py-1 text-right';
  const delBtn = document.createElement('button');
  delBtn.className = 'text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 text-xs px-2 py-1 rounded hover:bg-red-50';
  delBtn.textContent = '✕';
  delBtn.addEventListener('click', async () => {
    if (confirm(`Remove custom holiday "${holiday.name}" on ${holiday.date}?`)) {
      await Data.deleteCustomHoliday(holiday.id);
      await renderCustomHolidays();
    }
  });
  delTd.appendChild(delBtn);
  tr.appendChild(delTd);

  return tr;
}

function activateCatNameEdit(td, cat) {
  const original = cat.name;
  let cancelled = false;
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'cell-input';
  input.value = original;
  td.innerHTML = '';
  td.appendChild(input);
  input.focus();
  input.select();

  const commit = async () => {
    if (cancelled) return;
    const val = input.value.trim() || original;
    await Data.saveCategory({ ...cat, name: val });
    await renderCategories();
  };
  input.addEventListener('keydown', e => {
    if (e.key === 'Enter')  { commit(); input.blur(); }
    if (e.key === 'Escape') { cancelled = true; td.textContent = original; }
  });
  input.addEventListener('blur', commit);
}

// ── Data Export ───────────────────────────────────────────────────────────────

function renderDataExport() {
  const pane = document.getElementById('settings-data');
  pane.innerHTML = `
    <div class="bg-white rounded-xl border border-gray-200 p-4 max-w-sm">
      <h3 class="font-semibold text-gray-700 mb-1">Data Export</h3>
      <p class="text-xs text-gray-400 mb-4">Download all time entries for all users and all time as a CSV file.</p>
      <button id="btn-export-all" class="text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700">
        ⬇ Export all entries (CSV)
      </button>
      <span id="export-status" class="text-xs text-gray-400 ml-3 hidden">Preparing…</span>
    </div>
  `;
  pane.querySelector('#btn-export-all').addEventListener('click', async () => {
    const btn    = pane.querySelector('#btn-export-all');
    const status = pane.querySelector('#export-status');
    btn.disabled = true;
    status.classList.remove('hidden');
    try {
      await exportAllCSV();
    } finally {
      btn.disabled = false;
      status.classList.add('hidden');
    }
  });
}
